"use client";
import { jwtDecode } from "jwt-decode";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Users, UserPlus, UserCheck, Bell, BellOff } from "lucide-react";
import SocialPostCard from "../components/home_components/SocialPostCard";
import MainLayout from "../components/MainLayout";
import { successToast,errorToast } from '../utils/toasters'
import CreateGroupPostForm from "../components/scoops_components/CreateGroupPost";
import {
  handleGetGroupDetails,
  handleGetAllPosts,
  handleJoinGroup as apiJoinGroup,
  handleLeaveGroup as apiLeaveGroup,
} from "../services/group";
import followService from "../services/follow";

function GroupDetailPage() {
  const { id: groupId } = useParams();

  const [group, setGroup] = useState(null);
  const [postings, setPosts] = useState([]);
  const [followingUsers, setFollowingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [isNotificationsEnabled, setIsNotificationsEnabled] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [joinLeaveLoading, setJoinLeaveLoading] = useState(false);

  // 🎯 KEY FUNCTION: Fetch posts from the server
  const fetchPosts = async () => {
    try {
      const geoHash = "9v6m";
      const postsResponse = await handleGetAllPosts({ geoHash }, groupId);

      if (
        postsResponse &&
        postsResponse.status === "success" &&
        postsResponse.data
      ) {
        const postsData =
          postsResponse.data.post || postsResponse.data.posts || [];
        setPosts(postsData);
      }
    } catch (error) {
      console.error("❌ Error fetching posts:", error);
    }
  };

  // Initial data load when component mounts or groupId changes
  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        setLoading(true);
        const geoHash = localStorage.getItem("geohash") || "9v6m";
        const token = localStorage.getItem("access_token");
        let userId = null;
        if (token) {
          const decoded = jwtDecode(token);
          userId = decoded?.userid || decoded?.id || null;
        }
        const groupResponse = await handleGetGroupDetails({ userId }, groupId);

        if (
          groupResponse &&
          groupResponse.status === "success" &&
          groupResponse.data.group
        ) {
          const groupData = {
            id: groupResponse.data.group.groupid,
            title: groupResponse.data.group.name,
            description: groupResponse.data.group.description,
            coverImage: groupResponse.data.group.coverphoto,
            memberCount:
              parseInt(groupResponse.data.group.member_count, 10) || 0,
            isJoined: groupResponse.data.group.isJoined,
            createdAt: groupResponse.data.group.createdate,
          };

          setGroup(groupData);
          setIsJoined(groupResponse.data.group.isJoined);
        }

        // Fetch initial posts
        await fetchPosts();
      } catch (error) {
        console.error("❌ Error fetching group data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchGroupData();
    }
  }, [groupId, refreshKey]);

  // Fetch following users
  useEffect(() => {
    async function fetchFollowingUsers() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return;
      }
      const decodedToken = jwtDecode(token);
      const followings = await followService.handleGetUserFollowings(
        decodedToken.userid
      );
      const followingUsersId = followings.data.map(
        (follow) => follow.followeduserid
      );
      setFollowingUsers(followingUsersId);
    }
    fetchFollowingUsers();
  }, []);

  // Refetch data when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      fetchPosts();
    }
  }, [refreshKey]);

  // Join/leave group functionality
  const handleJoinOrLeaveGroup = async () => {
    try {
      setJoinLeaveLoading(true);

      if (isJoined) {
        const response = await apiLeaveGroup(groupId);

        if (response && response.status === "success") {
          setIsJoined(false);
          setGroup((prev) => ({
            ...prev,
            memberCount: Math.max(0, parseInt(prev.memberCount, 10) - 1),
          }));
          successToast("Successfully left the group", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          console.error("Failed to leave group:", response);
          errorToast("Failed to leave the group", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        const response = await apiJoinGroup(groupId);

        if (response && response.status === "success") {
          setIsJoined(true);
          setGroup((prev) => ({
            ...prev,
            memberCount: parseInt(prev.memberCount, 10) + 1,
          }));
          successToast("Successfully joined the group", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          console.error("Failed to join group:", response);
          errorToast("Failed to join the group", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error in join/leave group operation:", error);
      errorToast("An error occurred. Please try again later.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setJoinLeaveLoading(false);
    }
  };

  const handleToggleNotifications = () => {
    setIsNotificationsEnabled(!isNotificationsEnabled);
  };


  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse mb-6" />
        <div className="h-24 bg-gray-100 rounded-lg animate-pulse mb-6" />
        <div className="h-96 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="bg-gray-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-gray-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2 font-fraunces">
          Group not found
        </h1>
        <p className="text-gray-600 mb-6 font-afacad">
          The group you're looking for doesn't exist or you don't have access to
          it.
        </p>
        <Link
          to="/groups"
          className="inline-flex items-center px-4 py-2 bg-[#7B189F] text-white rounded-lg hover:bg-purple-700 transition-colors font-afacad"
        >
          Back to Groups
        </Link>
      </div>
    );
  }

  return (
    <MainLayout rs={false}>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Group Header */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
          <div className="relative h-64 w-full">
            <img
              src={group.coverImage || "/placeholder.svg"}
              alt={group.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="text-3xl font-bold text-white mb-2 font-fraunces">
                {group.title}
              </h1>

              <div className="flex items-center text-white/90">
                <Users className="h-5 w-5 mr-2" />
                <span className="font-afacad">{group.memberCount} members</span>
              </div>
            </div>
          </div>

          <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-grow">
              <p className="text-gray-700 font-afacad">{group.description}</p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleJoinOrLeaveGroup}
                disabled={joinLeaveLoading}
                className={`px-4 py-2 rounded-lg flex items-center font-afacad ${
                  joinLeaveLoading ? "opacity-70 cursor-not-allowed" : ""
                } ${
                  isJoined
                    ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    : "bg-[#7B189F] text-white hover:bg-purple-700"
                }`}
              >
                {joinLeaveLoading ? (
                  <span>Processing...</span>
                ) : isJoined ? (
                  <>
                    <UserCheck className="h-5 w-5 mr-1" />
                    <span>Joined</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="h-5 w-5 mr-1" />
                    <span>Join</span>
                  </>
                )}
              </button>

              <button
                onClick={handleToggleNotifications}
                className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                title={
                  isNotificationsEnabled
                    ? "Mute notifications"
                    : "Enable notifications"
                }
              >
                {isNotificationsEnabled ? (
                  <Bell className="h-5 w-5" />
                ) : (
                  <BellOff className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 🎯 KEY CHANGE: Pass fetchPosts as onPostSuccess */}
        {isJoined && (
          <CreateGroupPostForm
            groupTopic={group.topic}
            groupTopicId={group.topicId}
            id={groupId}
            followingUsers={followingUsers}
            postings={postings}
            onPostSuccess={fetchPosts} // 🚀 This triggers fresh data fetch
          />
        )}

        {/* Group Posts - Display with SocialPostCard */}
        <div className="space-y-6">
          {postings && postings.length > 0 ? (
            postings.map((post, index) => (
              <div key={post.id || post._id || post.postid || index}>
                <SocialPostCard
                  followingUsers={followingUsers}
                  post={post}
                  postings={postings}
                  setPostings={setPosts}
                  username={post.username}
                  images={post.photopath ? post.photopath.split(",") : []}
                />
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
              <div className="bg-gray-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1 font-fraunces">
                No posts yet
              </h3>
              <p className="text-gray-500 mb-4 font-afacad">
                Be the first to share something with the group!
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default GroupDetailPage;
